import React, { useEffect, useMemo, useState } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    MenuItem,
    Dialog,
    DialogContent,
    FormHelperText,
    FormControl,
    Select,
    InputLabel,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import Divider from '@mui/material/Divider';
import AppDialogTitle from '../../ui/appDialogTitle';
import { EmployeeService } from '../../../service/employeeService';
import { isLocalDev } from '../../../utils/environment';
import { toast } from 'react-toastify';
import { initialValues } from './commonEmployeeData';
import { newFormValidationSchema } from './employeeFormValidationSchema';
import ProvinceSelect from '../../common/provinceSelect';
import CitySelect from '../../common/citySelect';
import { passwordStrength } from '../../../utils';
import { PasswordStrengthLinearProgress } from '../../../styles/passwordStrengthProgress';
import { PasswordField } from '../../common/passwordField';
import SignatureDialog from '../../common/signatureDialog';
import SignatureDisplay from '../../common/signatureDisplay';
import PhoneNumberField from '../../common/phoneNumberField';
import { PASSWORD_POLICY_HINT } from '../../../utils/passwordPolicy';
import { usePreferences } from 'hooks/usePreferences';
import { ValidRoleService } from '../../../service/validDataService';
import SafeDialogContent from 'components/ui/SafeDialogContent';
import { COUNTRY_OPTIONS, getCountryId, normalizeCountryValue } from '../../../utils/preferencesOptions';

export default function EmployeeInputForm({ open, onClose, onUserAdded }) {
    const [passwordStrengthLevel, setPasswordStrengthLevel] = useState(null);
    const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
    const [capturedSignature, setCapturedSignature] = useState(null);
    const [roles, setRoles] = useState([]);

    // Get business preferences from the context (pre-loaded, no delay)
    const { preferences: businessPreferences } = usePreferences();

    // Get phone format from preferences or fallback to default
    const phoneFormat = businessPreferences?.phoneNumberFormat || '(###) ###-####';
    const employeeInitialValues = useMemo(() => ({
        ...initialValues,
        addrCountry: normalizeCountryValue(businessPreferences?.country)
    }), [businessPreferences?.country]);

    // Load roles dynamically from ValidRoleService
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await ValidRoleService.getAll();
                if (!mounted) return;
                setRoles(Array.isArray(data) ? data : []);
            } catch (_) {
                setRoles([]);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Handle signature capture from dialog
    const handleSignatureSave = (signatureData) => {
        // signatureData is an array, get the first item
        const signature = signatureData[0];
        
        let signatureWithPreview;
        
        if (signature.file) {
            // Hand-drawn signature with buffer data
            const blob = new Blob([new Uint8Array(signature.file)], { type: 'image/png' });
            const previewUrl = URL.createObjectURL(blob);
            
            signatureWithPreview = {
                ...signature,
                previewUrl: previewUrl
            };
        } else {
            // Uploaded signature with downloadurl
            signatureWithPreview = {
                ...signature,
                previewUrl: signature.downloadurl
            };
        }
        
        setCapturedSignature(signatureWithPreview);
    };

    // Handle signature deletion
    const handleSignatureDelete = () => {
        // Clean up the blob URL to prevent memory leaks
        if (capturedSignature && capturedSignature.previewUrl) {
            URL.revokeObjectURL(capturedSignature.previewUrl);
        }
        setCapturedSignature(null);
    };

    // Handle opening signature dialog
    const handleEditSignature = () => {
        setSignatureDialogOpen(true);
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        // Convert phone numbers to E.164 format for database storage
        const formData = { ...values };

        // phone formatting is done server side

        // Add captured signature to form values if exists
        if (capturedSignature) {
            formData.signature = capturedSignature;
        }

        try {
            const response = await EmployeeService.add(formData)
            const { code, data } = response

            setSubmitting(false)

            if (code === 0) {
                toast.success("Record added");
                resetForm();
                // Reset signature state
                setCapturedSignature(null);
                onUserAdded(data);
            } else {
                toast.success("Failed to add record");
            }
        } catch (error) {
            if (isLocalDev) {
                console.log(error);
            }
            setSubmitting(false)
        }
    };

    return (
        <Dialog open={open} maxWidth={'md'}>
            <AppDialogTitle title={"Add Employee"} onClose={onClose} />
            <SafeDialogContent>
                <Formik
                    initialValues={employeeInitialValues}
                    enableReinitialize
                    validationSchema={newFormValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4} mt={1}>
                                    <Field
                                        as={TextField}
                                        name="firstName"
                                        label="First Name"
                                        required
                                        fullWidth
                                    />
                                    <ErrorMessage name="firstName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={4} mt={1}>
                                    <Field
                                        as={TextField}
                                        name="middleName"
                                        label="Middle Name"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4} mt={1}>
                                    <Field
                                        as={TextField}
                                        name="lastName"
                                        label="Last Name"
                                        required
                                        fullWidth
                                    />
                                    <ErrorMessage name="lastName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider><Typography fontWeight='bold' variant='caption'>Address</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Field
                                        as={TextField}
                                        name="addrNo"
                                        label="Number"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrNo">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Field
                                        as={TextField}
                                        name="addrStreet"
                                        label="Street"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrStreet">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Field
                                        as={TextField}
                                        name="addrUnit"
                                        label="Unit"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrUnit">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        name="addrCity"
                                        component={CitySelect}
                                        size="large"
                                        countryId={getCountryId(values.addrCountry)}
                                        label="City"
                                    />
                                    <ErrorMessage name="addrCity">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        name="addrProvince"
                                        component={ProvinceSelect}
                                        size="large"
                                        countryId={getCountryId(values.addrCountry)}
                                        label="Province"
                                    />
                                    <ErrorMessage name="addrProvince">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Field
                                        as={TextField}
                                        name="addrPostal"
                                        label="Postal code"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrPostal">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Country</InputLabel>
                                        <Field name="addrCountry">
                                            {({ field, form }) => (
                                                <Select
                                                    {...field}
                                                    label="Country"
                                                    onChange={(e) => {
                                                        form.setFieldValue('addrCountry', e.target.value);
                                                        form.setFieldValue('addrCity', '');
                                                        form.setFieldValue('addrProvince', '');
                                                    }}
                                                >
                                                    {COUNTRY_OPTIONS.map((country) => (
                                                        <MenuItem key={country.value} value={country.value}>
                                                            {country.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        </Field>
                                    </FormControl>
                                    <ErrorMessage name="addrCountry">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider><Typography fontWeight='bold' variant='caption'>Contact info</Typography></Divider>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        name="phone"
                                        label="Phone Number"
                                        component={PhoneNumberField}
                                        mask={phoneFormat}
                                        countryCode={businessPreferences?.phoneNumberCountryCode || '+1'}
                                        required
                                        fullWidth
                                    />
                                    <ErrorMessage name="phone">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="email"
                                        label="Email"
                                        autoComplete="username"
                                        required
                                        fullWidth
                                    />
                                    <FormHelperText>Employees will use this to login to the system.</FormHelperText>
                                    <ErrorMessage name="email">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        name="password"
                                        label="Password"
                                        component={PasswordField}
                                        fieldName="password"
                                        autoComplete="new-password"
                                        values={values}
                                        required
                                        touched
                                        onChange={(e) => {
                                            //setFieldValue('password', e.target.value)
                                            setPasswordStrengthLevel(passwordStrength(e.target.value))
                                        }}
                                    //setFieldValue={setFieldValue} 
                                    />
                                    <FormHelperText>{PASSWORD_POLICY_HINT}</FormHelperText>
                                    <PasswordStrengthLinearProgress variant="determinate" value={passwordStrengthLevel} strength={passwordStrengthLevel} />
                                    <ErrorMessage name="password">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        name="confirmpassword"
                                        label="Confirm Password"
                                        component={PasswordField}
                                        fieldName="confirmpassword"
                                        autoComplete="new-password"
                                        values={values}
                                        required
                                        touched
                                        onChange={(e) => setFieldValue('confirmpassword', e.target.value)}
                                    //setFieldValue={setFieldValue} 
                                    />
                                    <ErrorMessage name="confirmpassword">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider><Typography fontWeight='bold' variant='caption'>Emergency contact</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="emergencyContactName1"
                                        label="Name"
                                        fullWidth
                                    />
                                    <ErrorMessage name="emergencyContactName1">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        name="emergencyContactPhone1"
                                        label="Phone"
                                        component={PhoneNumberField}
                                        mask={phoneFormat}
                                        countryCode={businessPreferences?.phoneNumberCountryCode || '+1'}
                                        fullWidth
                                    />
                                    <ErrorMessage name="emergencyContactPhone1">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="emergencyContactName2"
                                        label="Name"
                                        fullWidth
                                    />
                                    <ErrorMessage name="emergencyContactName2">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        name="emergencyContactPhone2"
                                        label="Phone"
                                        component={PhoneNumberField}
                                        mask={phoneFormat}
                                        countryCode={businessPreferences?.phoneNumberCountryCode || '+1'}
                                        fullWidth
                                    />
                                    <ErrorMessage name="emergencyContactPhone2">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="role"
                                        label="Role"
                                        select
                                        required
                                        fullWidth
                                    >
                                        {roles.map((r) => (
                                            <MenuItem key={r.id} value={r.name.toLowerCase()}>
                                                {r.name}
                                            </MenuItem>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="role">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                {values.role === 'practitioner' && (
                                    <Grid item xs={12}>
                                        <Divider><Typography fontWeight='bold' variant='caption'>Signature</Typography></Divider>
                                        <SignatureDisplay
                                            attachments={capturedSignature ? [{
                                                id: 'temp',
                                                path: capturedSignature.previewUrl,
                                                fileName: capturedSignature.originalname,
                                                contentType: 'image/png'
                                            }] : []}
                                            onEdit={handleEditSignature}
                                            onDelete={handleSignatureDelete}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        Create
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </SafeDialogContent>
            
            {/* Signature Capture Dialog */}
            <SignatureDialog
                open={signatureDialogOpen}
                onClose={() => setSignatureDialogOpen(false)}
                onSignatureSave={handleSignatureSave}
            />
        </Dialog>
    );
};